// CSR-773: pure helpers for the Documents tab. No React, no fetch — safe to
// unit-test directly. Date helpers render in the viewer's local timezone via
// Intl.DateTimeFormat. Locale is pinned to "en-US" so the spec-mandated
// month/day order and 12-hour time render consistently regardless of the
// browser's resolved locale (R-003).

const TABLE_TIMESTAMP_OPTS: Intl.DateTimeFormatOptions = {
  month: "numeric",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
};

const TIMELINE_TIMESTAMP_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
};

/**
 * Render a byte count as a human-readable string. Mirrors the format used in
 * the spec example: `pgr.pdf • 448.4 KB`.
 */
export function formatHumanFileSize(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

/**
 * Format a UTC ISO-string (or Date) into the table's "UPLOADED" cell shape:
 * `M/D/YYYY, h:mm:ss A`. Renders in the viewer's local timezone.
 */
export function formatTableTimestamp(value: string | Date | null | undefined): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", TABLE_TIMESTAMP_OPTS).format(date);
}

/**
 * Format a UTC ISO-string (or Date) into the timeline event's compact shape:
 * `MMM D, h:mm A`. Renders in the viewer's local timezone.
 */
export function formatTimelineTimestamp(value: string | Date | null | undefined): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", TIMELINE_TIMESTAMP_OPTS).format(date);
}

export function isImageMime(mimeType: string | null | undefined): boolean {
  if (!mimeType) return false;
  return /^image\/(png|jpe?g|gif|webp)$/i.test(mimeType);
}

export function isPdfMime(mimeType: string | null | undefined): boolean {
  return mimeType === "application/pdf";
}

// CSR-2056: the POLICY # cell. A document covers no policies (a W-9, or an
// upload the agent left unlinked), one, or several — a generated ACORD form can
// name every policy it was produced for. Which column those ids came from is
// already settled server-side by `resolveDocumentPolicyIds`; this is display
// only.
export const NO_POLICY_PLACEHOLDER = "—";

export function formatDocumentPolicyNumbers(
  policyNumbers: string[] | null | undefined,
): string {
  if (!policyNumbers || policyNumbers.length === 0) return NO_POLICY_PLACEHOLDER;
  return policyNumbers.join(", ");
}

/**
 * CSR-1257: format a policy's `effectiveDate` (a free-text column, usually an
 * ISO `YYYY-MM-DD`) into `M/D/YYYY` for the upload modal's policy dropdown.
 * Date-only strings are parsed by their parts to avoid a UTC→local off-by-one;
 * anything unparseable is returned verbatim.
 */
export function formatEffectiveDate(value: string | null | undefined): string {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  }).format(d);
}
